import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Helmet } from "react-helmet";
import {
  CreditCard,
  Check,
  Star,
  MessageCircle,
  Heart,
  ArrowLeft,
  Gift,
  Zap,
  Shield,
  Sparkles,
  Crown,
  Users,
  Globe,
  Smartphone,
  Lock,
  Award,
  TrendingUp,
  Clock,
  CheckCircle,
  ArrowRight,
  Star as StarIcon,
} from "lucide-react";

export default function PaymentGatewayApp() {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [currentFeature, setCurrentFeature] = useState(0);

  // Memoizar planes para mejor rendimiento
  const plans = useMemo(() => [
    {
      id: "basic",
      name: "Plan Premium VIP",
      price: 10000,
      priceUSD: 3.0,
      icon: Crown,
      color: "from-amber-500 via-orange-500 to-red-500",
      gradient: "from-yellow-400 via-orange-500 to-red-500",
      popular: true,
      features: [
        "👑 Plan Elite - Puntapués Deportivo",
        "🔓 Acceso ilimitado a todo el contenido premium",
        "🛠️ Soporte prioritario 24/7 — ¡Siempre contigo!",
        "💸 15% de descuento en todos los productos",
        "🎉 Eventos exclusivos y acceso anticipado a lanzamientos",
        "🚫 Sin anuncios — Disfruta sin interrupciones",
        "⬇️ Descargas offline para acceder donde quieras, cuando quieras",
      ],
      benefits: [
        { icon: TrendingUp, text: "95% de efectividad", color: "text-green-400" },
        { icon: Clock, text: "Activación inmediata", color: "text-blue-400" },
        { icon: Shield, text: "Garantía de 30 días", color: "text-purple-400" },
      ]
    },
  ], []);

  // Memoizar métodos de pago
  const paymentMethods = useMemo(() => [
    {
      id: "paypal",
      name: "PayPal",
      description: "PayPal y tarjetas internacionales",
      icon: Globe,
      color: "from-blue-500 to-blue-600",
      bgColor: "bg-blue-600",
      countries: "Internacional",
      fee: "0.0%",
      security: "SSL 256-bit",
      speed: "Instantáneo"
    },
    {
      id: "bold",
      name: "Bold",
      description: "PSE, Nequi, Daviplata, Bancolombia",
      icon: Smartphone,
      color: "from-green-500 to-emerald-600",
      bgColor: "bg-green-600",
      countries: "Colombia",
      fee: "0.0%",
      security: "PSE Seguro",
      speed: "Inmediato"
    },
  ], []);

  // Efecto para animaciones de entrada
  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Efecto para rotación automática de características
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentFeature((prev) => (prev + 1) % 3);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // Callbacks memoizados para mejor rendimiento
  const handlePlanSelect = useCallback((planId) => {
    setSelectedPlan(planId);
    setSelectedPayment(null);
  }, []);

  const handlePaymentSelect = useCallback((paymentId) => {
    setSelectedPayment(paymentId);
  }, []);

  const handlePayment = useCallback(async () => {
    if (!selectedPlan || !selectedPayment) return;

    setIsProcessing(true);

    const plan = plans.find((p) => p.id === selectedPlan);
    const payment = paymentMethods.find((p) => p.id === selectedPayment);

    // Simular delay de procesamiento
    await new Promise((resolve) => setTimeout(resolve, 2000));

    try {
      switch (selectedPayment) {
        case "paypal":
          const paypalUrl = `https://www.paypal.com/ncp/payment/86ERYR3DKPQT4`;
          window.open(paypalUrl, "_blank");
          break;

        case "bold":
          const boldUrl = "https://checkout.bold.co/payment/LNK_8PEIB1JEVZ";
          window.open(boldUrl, "_blank");
          break;

        default:
          throw new Error("Método de pago no válido");
      }
    } catch (error) {
      alert("Error al procesar el pago: " + error.message);
    } finally {
      setIsProcessing(false);
    }
  }, [selectedPlan, selectedPayment, plans, paymentMethods]);

  const handleWhatsApp = useCallback(() => {
    const plan = plans.find((p) => p.id === selectedPlan);
    const message = `Hola, necesito ayuda con el pago del ${plan?.name}`;
    window.open(
      `https://wa.me/573023580862?text=${encodeURIComponent(message)}`,
      "_blank"
    );
  }, [selectedPlan, plans]);

  const handleBack = useCallback(() => {
    if (selectedPayment) {
      setSelectedPayment(null);
    } else if (selectedPlan) {
      setSelectedPlan(null);
    } else {
      window.history.back();
    }
  }, [selectedPlan, selectedPayment]);

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Plan Premium VIP - DeportivosPro | Pronósticos Deportivos de Élite</title>
        <meta name="description" content="Desbloquea el poder VIP con nuestro Plan Premium. Acceso a pronósticos con 95% de efectividad, soporte 24/7, sin anuncios y beneficios exclusivos. ¡Activa tu membresía VIP ahora!" />
        <meta name="keywords" content="plan vip, membresía premium, pronósticos deportivos, apuestas deportivas, DeportivosPro, plan elite, puntapués deportivo, soporte 24/7" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Plan Premium VIP - DeportivosPro" />
        <meta property="og:description" content="Accede a pronósticos deportivos de élite con 95% de efectividad. Plan Premium VIP con soporte 24/7 y beneficios exclusivos." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://deportivospro.com/payment" />
        <link rel="canonical" href="https://deportivospro.com/payment" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            "name": "Plan Premium VIP DeportivosPro",
            "description": "Membresía premium con acceso a pronósticos deportivos de élite",
            "offers": {
              "@type": "Offer",
              "price": "30000",
              "priceCurrency": "COP",
              "availability": "https://schema.org/InStock"
            }
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-gray-950 via-black to-indigo-950 text-white overflow-hidden">
        {/* Fondo animado con partículas */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/20 via-purple-900/20 to-pink-900/20 animate-pulse"></div>
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-yellow-400 rounded-full animate-float opacity-60"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${i * 0.5}s`,
                animationDuration: `${3 + Math.random() * 2}s`
              }}
            />
          ))}
        </div>

        {/* Header mejorado */}
        <header className="relative bg-gradient-to-r from-indigo-900/95 via-purple-900/95 to-pink-900/95 backdrop-blur-md shadow-2xl w-full py-4 px-4 sm:px-6 sticky top-0 z-50 border-b border-indigo-500/30">
          <div className="max-w-6xl mx-auto flex justify-between items-center">
            <button
              onClick={handleBack}
              className="flex items-center text-indigo-200 hover:text-white transition-all duration-300 hover:scale-105 group"
            >
              <ArrowLeft className="h-5 w-5 mr-2 group-hover:-translate-x-1 transition-transform" />
              <span className="font-medium">
                {selectedPayment
                  ? "Cambiar método"
                  : selectedPlan
                  ? "Cambiar plan"
                  : "Regresar"}
              </span>
            </button>
            <div className="flex items-center space-x-2 bg-gradient-to-r from-green-500/20 to-emerald-500/20 px-3 py-1 rounded-full border border-green-500/30">
              <Shield className="h-5 w-5 text-green-400 animate-pulse" />
              <span className="text-sm font-medium text-green-300">
                🔒 Pago 100% Seguro
              </span>
            </div>
          </div>
        </header>

        <div className="relative z-10 flex-1 flex flex-col items-center justify-center py-4 sm:py-8 px-2 sm:px-4">
          {/* Selección de Plan */}
          {!selectedPlan && (
            <div className={`w-full max-w-5xl transform transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}>
              <div className="text-center mb-8 sm:mb-12 px-2">
                <div className="inline-flex items-center bg-gradient-to-r from-yellow-500 to-orange-500 text-black text-xs sm:text-sm font-bold px-3 sm:px-4 py-2 rounded-full mb-4 animate-pulse">
                  <StarIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-2 animate-spin" />
                  ¡OFERTA ESPECIAL LIMITADA!
                </div>
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 bg-clip-text text-transparent animate-gradient-x px-2">
                  Desbloquea el PODER VIP
                </h1>
                <p className="text-gray-300 text-base sm:text-lg md:text-xl lg:text-2xl max-w-3xl mx-auto leading-relaxed px-2">
                  Accede a pronósticos deportivos de <span className="text-yellow-400 font-bold">95% de efectividad</span> y 
                  beneficios exclusivos que transformarán tu experiencia
                </p>
              </div>

              {/* Estadísticas destacadas */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-8 sm:mb-12 px-2">
                {[
                  { icon: TrendingUp, value: "95%", label: "Efectividad", color: "from-green-500 to-emerald-500" },
                  { icon: Users, value: "10K+", label: "Usuarios VIP", color: "from-blue-500 to-indigo-500" },
                  { icon: Award, value: "24/7", label: "Soporte", color: "from-purple-500 to-pink-500" }
                ].map((stat, index) => (
                  <div
                    key={index}
                    className={`bg-gradient-to-br ${stat.color} p-4 sm:p-6 rounded-2xl text-center transform transition-all duration-500 hover:scale-105 hover:shadow-2xl`}
                    style={{ animationDelay: `${index * 200}ms` }}
                  >
                    <stat.icon className="h-6 w-6 sm:h-8 sm:w-8 text-white mx-auto mb-2 sm:mb-3" />
                    <div className="text-2xl sm:text-3xl font-bold text-white mb-1">{stat.value}</div>
                    <div className="text-white/90 font-medium text-sm sm:text-base">{stat.label}</div>
                  </div>
                ))}
              </div>

              <div className="grid md:grid-cols-1 gap-6 sm:gap-8 px-2">
                {plans.map((plan, index) => {
                  const IconComponent = plan.icon;
                  return (
                    <div
                      key={plan.id}
                      className={`relative bg-gradient-to-b from-gray-900/90 to-gray-800/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden border border-gray-600/30 transform transition-all duration-700 hover:shadow-2xl hover:shadow-yellow-500/20 hover:scale-105 cursor-pointer group ${
                        plan.popular ? "ring-2 ring-yellow-500 ring-opacity-50" : ""
                      }`}
                      onClick={() => handlePlanSelect(plan.id)}
                      style={{ animationDelay: `${index * 300}ms` }}
                    >
                      {/* Badge popular */}
                      {plan.popular && (
                        <div className="absolute top-3 sm:top-6 right-3 sm:right-6 bg-gradient-to-r from-yellow-500 to-orange-500 text-black px-2 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm font-bold rounded-full shadow-lg animate-pulse z-10">
                          ⭐ MÁS POPULAR
                        </div>
                      )}

                      {/* Header del plan */}
                      <div className={`bg-gradient-to-r ${plan.color} px-4 sm:px-6 md:px-8 py-8 sm:py-10 md:py-12 text-center relative overflow-hidden`}>
                        {/* Efectos de brillo */}
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent transform -skew-x-12 animate-shine"></div>
                        <div className="absolute -top-10 sm:-top-20 -right-10 sm:-right-20 w-20 h-20 sm:w-40 sm:h-40 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
                        
                        <IconComponent className="h-12 w-12 sm:h-16 sm:w-16 text-white mx-auto mb-3 sm:mb-4 drop-shadow-2xl animate-bounce" />
                        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4 text-white drop-shadow-lg px-2">{plan.name}</h2>
                        
                        {/* Precio con animación */}
                        <div className="mb-2 sm:mb-3">
                          <span className="text-3xl sm:text-4xl md:text-5xl font-bold text-white drop-shadow-lg">
                            ${plan.price.toLocaleString()} COP
                          </span>
                          <span className="text-base sm:text-lg md:text-xl ml-1 sm:ml-2 opacity-90">/mes</span>
                        </div>
                        <div className="text-sm sm:text-base md:text-lg opacity-90 font-medium">
                          ${plan.priceUSD} USD/mes
                        </div>
                      </div>

                      {/* Contenido del plan */}
                      <div className="px-4 sm:px-6 md:px-8 py-6 sm:py-8">
                        {/* Beneficios destacados */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6">
                          {plan.benefits.map((benefit, idx) => (
                            <div key={idx} className="text-center p-2 sm:p-3 bg-gradient-to-br from-gray-800/50 to-gray-700/50 rounded-xl border border-gray-600/30">
                              <benefit.icon className={`h-5 w-5 sm:h-6 sm:w-6 mx-auto mb-2 ${benefit.color}`} />
                              <p className="text-xs sm:text-sm font-medium text-gray-200">{benefit.text}</p>
                            </div>
                          ))}
                        </div>

                        {/* Lista de características */}
                        <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
                          {plan.features.map((feature, index) => (
                            <div 
                              key={index} 
                              className="flex items-center p-2 sm:p-3 bg-gradient-to-r from-gray-800/30 to-gray-700/30 rounded-xl border border-gray-600/20 hover:border-yellow-500/30 transition-all duration-300 group"
                            >
                              <div className="bg-gradient-to-r from-green-500 to-emerald-500 p-1.5 sm:p-2 rounded-full mr-3 sm:mr-4 group-hover:scale-110 transition-transform flex-shrink-0">
                                <Check className="h-3 w-3 sm:h-4 sm:w-4 text-white" />
                              </div>
                              <p className="text-gray-200 text-sm sm:text-base group-hover:text-white transition-colors">
                                {feature}
                              </p>
                            </div>
                          ))}
                        </div>

                        {/* Botón de selección */}
                        <button className={`w-full py-3 sm:py-4 bg-gradient-to-r ${plan.gradient} hover:from-yellow-300 hover:via-orange-400 hover:to-red-400 text-black font-bold text-base sm:text-lg rounded-xl sm:rounded-2xl shadow-2xl transition-all duration-500 transform hover:scale-105 hover:shadow-yellow-500/50 group`}>
                          <span className="flex items-center justify-center">
                            <span className="text-sm sm:text-base">🚀 ACTIVAR PLAN VIP AHORA</span>
                            <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5 group-hover:translate-x-1 transition-transform" />
                          </span>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Selección de Método de Pago */}
          {selectedPlan && !selectedPayment && (
            <div className={`w-full max-w-3xl transform transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}>
              <div className="text-center mb-8 sm:mb-10 px-2">
                <h2 className="text-3xl sm:text-4xl font-bold mb-4 bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">
                  Método de Pago
                </h2>
                <p className="text-gray-300 text-lg sm:text-xl px-2">
                  Selecciona cómo deseas pagar tu{" "}
                  <span className="text-yellow-400 font-bold">
                    {plans.find((p) => p.id === selectedPlan)?.name}
                  </span>
                </p>
              </div>

              <div className="space-y-4 sm:space-y-6 px-2">
                {paymentMethods.map((method, index) => {
                  const IconComponent = method.icon;
                  return (
                    <div
                      key={method.id}
                      className={`bg-gradient-to-r from-gray-900/90 to-gray-800/90 backdrop-blur-sm rounded-xl sm:rounded-2xl border border-gray-600/30 p-4 sm:p-6 md:p-8 hover:border-yellow-500/50 transition-all duration-500 cursor-pointer transform hover:scale-105 hover:shadow-2xl hover:shadow-yellow-500/20 group`}
                      onClick={() => handlePaymentSelect(method.id)}
                      style={{ animationDelay: `${index * 200}ms` }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center flex-1 min-w-0">
                          <div className={`bg-gradient-to-r ${method.color} p-3 sm:p-4 rounded-xl sm:rounded-2xl mr-4 sm:mr-6 group-hover:scale-110 transition-transform duration-300 flex-shrink-0`}>
                            <IconComponent className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white mb-1 sm:mb-2 truncate">
                              {method.name}
                            </h3>
                            <p className="text-gray-300 text-sm sm:text-base md:text-lg mb-2 sm:mb-3">{method.description}</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4">
                              <div className="flex items-center">
                                <Globe className="h-3 w-3 sm:h-4 sm:w-4 text-blue-400 mr-1 sm:mr-2 flex-shrink-0" />
                                <span className="text-xs sm:text-sm text-blue-300 truncate">{method.countries}</span>
                              </div>
                              <div className="flex items-center">
                                <Shield className="h-3 w-3 sm:h-4 sm:w-4 text-green-400 mr-1 sm:mr-2 flex-shrink-0" />
                                <span className="text-xs sm:text-sm text-green-300 truncate">{method.security}</span>
                              </div>
                              <div className="flex items-center">
                                <Zap className="h-3 w-3 sm:h-4 sm:w-4 text-yellow-400 mr-1 sm:mr-2 flex-shrink-0" />
                                <span className="text-xs sm:text-sm text-yellow-300 truncate">{method.speed}</span>
                              </div>
                              <div className="flex items-center">
                                <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 text-purple-400 mr-1 sm:mr-2 flex-shrink-0" />
                                <span className="text-xs sm:text-sm text-purple-300 truncate">Comisión: {method.fee}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <ArrowRight className="h-5 w-5 sm:h-6 sm:w-6 text-gray-400 transform rotate-180 group-hover:translate-x-1 transition-transform flex-shrink-0 ml-2" />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Confirmación de Pago */}
          {selectedPlan && selectedPayment && (
            <div className={`w-full max-w-lg transform transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}>
              <div className="bg-gradient-to-b from-gray-900/90 to-gray-800/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden border border-gray-600/30 mx-2">
                <div className="bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 px-4 sm:px-6 md:px-8 py-6 sm:py-8 text-center relative overflow-hidden">
                  {/* Efectos de brillo */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent transform -skew-x-12 animate-shine"></div>
                  <h2 className="text-2xl sm:text-3xl font-bold mb-2 sm:mb-3 text-black">🎯 Confirmar Pago</h2>
                  <p className="text-black/80 text-base sm:text-lg font-medium">
                    Revisa los detalles antes de continuar
                  </p>
                </div>

                <div className="px-4 sm:px-6 md:px-8 py-6 sm:py-8 space-y-4 sm:space-y-6">
                  {/* Plan seleccionado */}
                  <div className="bg-gradient-to-r from-gray-800/50 to-gray-700/50 p-4 sm:p-6 rounded-xl sm:rounded-2xl border border-gray-600/30">
                    <h3 className="font-bold text-base sm:text-lg mb-2 sm:mb-3 text-yellow-400">📋 Plan Seleccionado</h3>
                    <p className="text-gray-300 text-base sm:text-lg mb-1 sm:mb-2">
                      {plans.find((p) => p.id === selectedPlan)?.name}
                    </p>
                    <p className="text-2xl sm:text-3xl font-bold text-yellow-400">
                      ${plans.find((p) => p.id === selectedPlan)?.price.toLocaleString()} COP
                    </p>
                  </div>

                  {/* Método de pago */}
                  <div className="bg-gradient-to-r from-gray-800/50 to-gray-700/50 p-4 sm:p-6 rounded-xl sm:rounded-2xl border border-gray-600/30">
                    <h3 className="font-bold text-base sm:text-lg mb-2 sm:mb-3 text-blue-400">💳 Método de Pago</h3>
                    <div className="flex items-center">
                      <div className={`bg-gradient-to-r ${
                        paymentMethods.find((p) => p.id === selectedPayment)?.color
                      } p-2 sm:p-3 rounded-lg sm:rounded-xl mr-3 sm:mr-4 flex-shrink-0`}>
                        {React.createElement(
                          paymentMethods.find((p) => p.id === selectedPayment)?.icon,
                          { className: "h-4 w-4 sm:h-6 sm:w-6 text-white" }
                        )}
                      </div>
                      <span className="text-base sm:text-lg text-gray-200 truncate">
                        {paymentMethods.find((p) => p.id === selectedPayment)?.name}
                      </span>
                    </div>
                  </div>

                  {/* Botones */}
                  <div className="space-y-3 sm:space-y-4 mt-6 sm:mt-8">
                    <button
                      onClick={handlePayment}
                      disabled={isProcessing}
                      className="w-full py-4 sm:py-5 bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 hover:from-yellow-400 hover:via-orange-400 hover:to-red-400 disabled:opacity-50 disabled:cursor-not-allowed text-black font-bold text-base sm:text-lg rounded-xl sm:rounded-2xl shadow-2xl transition-all duration-500 transform hover:scale-105 flex items-center justify-center group"
                    >
                      {isProcessing ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 sm:h-6 sm:w-6 border-b-2 border-black mr-2 sm:mr-3"></div>
                          <span className="text-sm sm:text-base">Procesando Pago...</span>
                        </>
                      ) : (
                        <>
                          <CreditCard className="h-5 w-5 sm:h-6 sm:w-6 mr-2 sm:mr-3 group-hover:scale-110 transition-transform" />
                          <span className="text-sm sm:text-base">🚀 Proceder al Pago</span>
                        </>
                      )}
                    </button>

                    <button
                      onClick={handleWhatsApp}
                      className="w-full py-3 sm:py-4 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-400 hover:to-emerald-500 text-white font-bold text-base sm:text-lg rounded-xl sm:rounded-2xl shadow-2xl transition-all duration-500 flex items-center justify-center transform hover:scale-105 group"
                    >
                      <MessageCircle className="h-5 w-5 sm:h-6 sm:w-6 mr-2 sm:mr-3 group-hover:scale-110 transition-transform" />
                      <span className="text-sm sm:text-base">💬 Ayuda por WhatsApp</span>
                    </button>
                  </div>
                </div>

                {/* Footer */}
                <div className="px-4 sm:px-6 md:px-8 py-4 sm:py-6 border-t border-gray-600/30 bg-gradient-to-r from-gray-900/50 to-gray-800/50">
                  <div className="grid grid-cols-2 gap-3 sm:gap-4 text-xs sm:text-sm text-gray-400">
                    <div className="flex items-center justify-center">
                      <Shield className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2 text-green-400" />
                      <span>Pago Seguro</span>
                    </div>
                    <div className="flex items-center justify-center">
                      <Zap className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2 text-yellow-400" />
                      <span>Activación Inmediata</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Estilos CSS personalizados */}
        <style jsx>{`
          @keyframes gradient-x {
            0%, 100% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
          }
          
          @keyframes shine {
            0% { transform: translateX(-100%) skewX(-12deg); }
            100% { transform: translateX(200%) skewX(-12deg); }
          }
          
          @keyframes float {
            0%, 100% { transform: translateY(0px) rotate(0deg); }
            50% { transform: translateY(-20px) rotate(180deg); }
          }
          
          .animate-gradient-x {
            background-size: 200% 200%;
            animation: gradient-x 15s ease infinite;
          }
          
          .animate-shine {
            animation: shine 2s ease-in-out infinite;
          }
          
          .animate-float {
            animation: float 3s ease-in-out infinite;
          }
        `}</style>
      </div>
    </>
  );
}
